// Serviço do domínio communications — central de mensagens com triagem
// e aprovação médica para respostas clínicas.
import { createClient } from '@/lib/supabase/client'
import { registrarAuditoria } from '@/services/identity'
import { notificar } from '@/services/notificacoes'
import { criarTarefa } from '@/services/tarefas'
import { registrarEvento } from '@/services/timeline'

export type CategoriaMensagem =
  | 'duvida_administrativa' | 'duvida_medicacao' | 'envio_exame' | 'novo_sintoma'
  | 'solicitacao_receita' | 'pedido_documento' | 'solicitacao_retorno' | 'intercorrencia'
  | 'orientacao' | 'outra'

export type StatusThread =
  | 'recebida' | 'em_triagem' | 'aguardando_secretaria' | 'aguardando_medico'
  | 'resposta_preparada' | 'aguardando_aprovacao' | 'respondida' | 'encerrada' | 'convertida_tarefa'

export type CanalMensagem = 'app' | 'whatsapp' | 'telefone' | 'presencial' | 'email'

export interface MensagemThread {
  id: string
  workspace_id: string
  paciente_id: string
  consulta_id: string | null
  assunto: string | null
  categoria: CategoriaMensagem
  prioridade: 'baixa' | 'normal' | 'alta' | 'urgente'
  status: StatusThread
  responsavel_user_id: string | null
  prazo: string | null
  tarefa_id: string | null
  criado_por: string | null
  created_at: string
}

export interface Mensagem {
  id: string
  thread_id: string
  autor_user_id: string | null
  autor_tipo: 'paciente' | 'equipe'
  canal: CanalMensagem
  conteudo: string
  rascunho: boolean
  aprovada_por: string | null
  aprovada_em: string | null
  created_at: string
  // WhatsApp (phase 8) — nulos em mensagens registradas manualmente
  direcao?: 'entrada' | 'saida'
  wa_message_id?: string | null
  status_entrega?: 'pendente' | 'enviada' | 'entregue' | 'lida' | 'falhou' | null
  erro?: string | null
  template_nome?: string | null
  midia?: { tipo: string; media_id: string | null } | null
}

export interface MensagemModelo {
  id: string
  titulo: string
  conteudo: string
  ativo: boolean
}

export const CATEGORIA_MSG_LABEL: Record<CategoriaMensagem, string> = {
  duvida_administrativa: 'Dúvida administrativa',
  duvida_medicacao: 'Dúvida sobre medicação',
  envio_exame: 'Envio de exame',
  novo_sintoma: 'Novo sintoma',
  solicitacao_receita: 'Solicitação de receita',
  pedido_documento: 'Pedido de documento',
  solicitacao_retorno: 'Solicitação de retorno',
  intercorrencia: 'Intercorrência',
  orientacao: 'Orientação',
  outra: 'Outra',
}

export const STATUS_THREAD_LABEL: Record<StatusThread, string> = {
  recebida: 'Recebida',
  em_triagem: 'Em triagem',
  aguardando_secretaria: 'Aguardando secretária',
  aguardando_medico: 'Aguardando médico',
  resposta_preparada: 'Resposta preparada',
  aguardando_aprovacao: 'Aguardando aprovação',
  respondida: 'Respondida',
  encerrada: 'Encerrada',
  convertida_tarefa: 'Convertida em pendência',
}

export const CANAL_LABEL: Record<CanalMensagem, string> = {
  app: 'App',
  whatsapp: 'WhatsApp',
  telefone: 'Telefone',
  presencial: 'Presencial',
  email: 'E-mail',
}

export const STATUS_THREAD_ABERTOS: StatusThread[] = [
  'recebida', 'em_triagem', 'aguardando_secretaria', 'aguardando_medico',
  'resposta_preparada', 'aguardando_aprovacao',
]

export interface ThreadComPaciente extends MensagemThread {
  paciente_nome: string
}

function mapThread(row: Record<string, unknown>): ThreadComPaciente {
  const raw = row.pacientes
  const pac = (Array.isArray(raw) ? raw[0] : raw) as { nome: string } | null
  const { pacientes: _drop, ...resto } = row
  void _drop
  return { ...(resto as unknown as MensagemThread), paciente_nome: pac?.nome ?? '—' }
}

export async function listThreads(workspaceId: string, filtro: {
  aba?: 'abertas' | 'encerradas' | 'todas'
  categoria?: CategoriaMensagem | 'todas'
} = {}): Promise<ThreadComPaciente[]> {
  const supabase = createClient()
  let q = supabase
    .from('mensagem_threads')
    .select('*, pacientes(nome)')
    .eq('workspace_id', workspaceId)
    .order('updated_at', { ascending: false })
    .limit(300)
  if (filtro.aba === 'abertas' || filtro.aba === undefined) q = q.in('status', STATUS_THREAD_ABERTOS)
  else if (filtro.aba === 'encerradas') q = q.in('status', ['respondida', 'encerrada', 'convertida_tarefa'])
  if (filtro.categoria && filtro.categoria !== 'todas') q = q.eq('categoria', filtro.categoria)
  const { data, error } = await q
  if (error) throw error
  return (data ?? []).map(r => mapThread(r as Record<string, unknown>))
}

export async function getThread(workspaceId: string, id: string): Promise<{
  thread: ThreadComPaciente
  mensagens: Mensagem[]
} | null> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('mensagem_threads')
    .select('*, pacientes(nome)')
    .eq('workspace_id', workspaceId)
    .eq('id', id)
    .maybeSingle()
  if (error) throw error
  if (!data) return null
  const { data: msgs } = await supabase
    .from('mensagens')
    .select('*')
    .eq('thread_id', id)
    .order('created_at')
  return {
    thread: mapThread(data as Record<string, unknown>),
    mensagens: (msgs ?? []) as Mensagem[],
  }
}

/** Registra uma mensagem recebida do paciente (por WhatsApp/telefone/etc.). */
export async function registrarRecebida(params: {
  workspaceId: string
  pacienteId: string
  categoria: CategoriaMensagem
  canal: CanalMensagem
  conteudo: string
  assunto?: string | null
  prioridade?: 'baixa' | 'normal' | 'alta' | 'urgente'
}): Promise<string> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('mensagem_threads')
    .insert({
      workspace_id: params.workspaceId,
      paciente_id: params.pacienteId,
      categoria: params.categoria,
      prioridade: params.prioridade ?? 'normal',
      assunto: params.assunto ?? null,
      status: 'recebida',
    })
    .select('id')
    .single()
  if (error) throw error
  const threadId = (data as { id: string }).id

  const { error: e2 } = await supabase.from('mensagens').insert({
    workspace_id: params.workspaceId,
    thread_id: threadId,
    autor_tipo: 'paciente',
    canal: params.canal,
    conteudo: params.conteudo,
  })
  if (e2) throw e2

  await registrarEvento({
    workspaceId: params.workspaceId,
    pacienteId: params.pacienteId,
    tipo: 'mensagem_recebida',
    titulo: `Mensagem recebida (${CATEGORIA_MSG_LABEL[params.categoria]})`,
    eventoDominio: 'message.received',
  })
  await registrarAuditoria({
    workspaceId: params.workspaceId,
    acao: 'mensagem_recebida',
    entidade: 'mensagem_threads',
    registroId: threadId,
  })
  return threadId
}

export async function triagemThread(params: {
  workspaceId: string
  threadId: string
  patch: Partial<Pick<MensagemThread, 'categoria' | 'prioridade' | 'status' | 'responsavel_user_id' | 'prazo' | 'assunto'>>
  notificarUserId?: string | null
  tituloNotificacao?: string
}): Promise<void> {
  const supabase = createClient()
  const { error } = await supabase
    .from('mensagem_threads')
    .update(params.patch)
    .eq('id', params.threadId)
    .eq('workspace_id', params.workspaceId)
  if (error) throw error
  if (params.notificarUserId) {
    await notificar({
      workspaceId: params.workspaceId,
      userId: params.notificarUserId,
      tipo: 'mensagem_triagem',
      titulo: params.tituloNotificacao ?? 'Mensagem encaminhada a você',
      link: `/mensagens/${params.threadId}`,
    })
  }
  await registrarAuditoria({
    workspaceId: params.workspaceId,
    acao: 'mensagem_triagem',
    entidade: 'mensagem_threads',
    registroId: params.threadId,
    dados: params.patch as Record<string, unknown>,
  })
}

/**
 * Responde a thread.
 * - modo 'administrativa': mensagem sai direto (qualquer membro).
 * - modo 'clinica_rascunho': secretária prepara; fica aguardando aprovação médica.
 * - modo 'clinica_direta': médico responde direto (já aprovada).
 */
export async function responderThread(params: {
  workspaceId: string
  thread: MensagemThread
  conteudo: string
  modo: 'administrativa' | 'clinica_rascunho' | 'clinica_direta'
  medicoTitularUserId?: string | null
}): Promise<void> {
  const supabase = createClient()
  const rascunho = params.modo === 'clinica_rascunho'
  const { error } = await supabase.from('mensagens').insert({
    workspace_id: params.workspaceId,
    thread_id: params.thread.id,
    autor_tipo: 'equipe',
    conteudo: params.conteudo.trim(),
    rascunho,
  })
  if (error) throw error

  const novoStatus: StatusThread = rascunho ? 'aguardando_aprovacao' : 'respondida'
  await supabase
    .from('mensagem_threads')
    .update({ status: novoStatus })
    .eq('id', params.thread.id)
    .eq('workspace_id', params.workspaceId)

  if (rascunho && params.medicoTitularUserId) {
    await notificar({
      workspaceId: params.workspaceId,
      userId: params.medicoTitularUserId,
      tipo: 'resposta_aguardando_aprovacao',
      titulo: 'Resposta clínica aguardando sua aprovação',
      link: `/mensagens/${params.thread.id}`,
    })
  }
  if (!rascunho) {
    await registrarEvento({
      workspaceId: params.workspaceId,
      pacienteId: params.thread.paciente_id,
      tipo: 'mensagem_respondida',
      titulo: 'Mensagem respondida',
    })
  }
  await registrarAuditoria({
    workspaceId: params.workspaceId,
    acao: rascunho ? 'resposta_preparada' : 'mensagem_respondida',
    entidade: 'mensagem_threads',
    registroId: params.thread.id,
  })
}

/** Médico aprova o rascunho (o banco valida o papel) — a resposta é liberada. */
export async function aprovarRascunho(params: {
  workspaceId: string
  thread: MensagemThread
  mensagem: Mensagem
  notificarUserId?: string | null
}): Promise<void> {
  const supabase = createClient()
  const { error } = await supabase
    .from('mensagens')
    .update({ rascunho: false })
    .eq('id', params.mensagem.id)
    .eq('workspace_id', params.workspaceId)
  if (error) throw error
  await supabase
    .from('mensagem_threads')
    .update({ status: 'respondida' })
    .eq('id', params.thread.id)
    .eq('workspace_id', params.workspaceId)
  if (params.notificarUserId) {
    await notificar({
      workspaceId: params.workspaceId,
      userId: params.notificarUserId,
      tipo: 'resposta_aprovada',
      titulo: 'Resposta clínica aprovada — pode enviar à paciente',
      link: `/mensagens/${params.thread.id}`,
    })
  }
  await registrarEvento({
    workspaceId: params.workspaceId,
    pacienteId: params.thread.paciente_id,
    tipo: 'mensagem_respondida',
    titulo: 'Resposta clínica aprovada pelo médico',
  })
  await registrarAuditoria({
    workspaceId: params.workspaceId,
    acao: 'resposta_aprovada',
    entidade: 'mensagens',
    registroId: params.mensagem.id,
  })
}

/** Converte a thread em pendência estruturada, mantendo o vínculo. */
export async function converterEmTarefa(params: {
  workspaceId: string
  thread: ThreadComPaciente
  criadorUserId: string
  responsavelUserId?: string | null
  prazo?: string | null
}): Promise<string> {
  const tarefaId = await criarTarefa({
    workspaceId: params.workspaceId,
    criadorUserId: params.criadorUserId,
    dados: {
      titulo: `Responder ${params.thread.paciente_nome}: ${params.thread.assunto ?? CATEGORIA_MSG_LABEL[params.thread.categoria]}`,
      categoria: 'responder_paciente',
      prioridade: params.thread.prioridade,
      paciente_id: params.thread.paciente_id,
      responsavel_user_id: params.responsavelUserId ?? null,
      prazo: params.prazo ?? params.thread.prazo,
    },
  })
  const supabase = createClient()
  await supabase
    .from('mensagem_threads')
    .update({ status: 'convertida_tarefa', tarefa_id: tarefaId })
    .eq('id', params.thread.id)
    .eq('workspace_id', params.workspaceId)
  await registrarAuditoria({
    workspaceId: params.workspaceId,
    acao: 'mensagem_convertida_tarefa',
    entidade: 'mensagem_threads',
    registroId: params.thread.id,
    dados: { tarefa_id: tarefaId },
  })
  return tarefaId
}

// ---------- modelos ----------

export async function listModelos(workspaceId: string): Promise<MensagemModelo[]> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('mensagem_modelos')
    .select('id, titulo, conteudo, ativo')
    .eq('workspace_id', workspaceId)
    .eq('ativo', true)
    .order('titulo')
  if (error) throw error
  return (data ?? []) as MensagemModelo[]
}

export async function criarModelo(workspaceId: string, titulo: string, conteudo: string): Promise<void> {
  const supabase = createClient()
  const { error } = await supabase.from('mensagem_modelos').insert({
    workspace_id: workspaceId,
    titulo: titulo.trim(),
    conteudo: conteudo.trim(),
  })
  if (error) throw error
}
